// popstate-based router for the rust-dashboard SPA.
//
// Design rationale (from openspec/changes/ui-redesign-v2/design.md,
// ADR-2): we use the HTML5 history API rather than hash routing so
// deep-links like `/containers/sola-app` survive the Traefik SPA
// fallback. The router is intentionally tiny — about 80 lines — and
// exposes both a pure `resolveRoute(pathname)` function (for tests and
// SSR-style rendering) and an imperative `push`/`subscribe` bridge
// for components.
//
// Routes (6, focused on actionable container operations):
//
//   /                  overview
//   /host              host
//   /containers        containers
//   /containers/:id    container-detail
//   /audit             audit
//   /login             login (auth gate — public)
//
// Removed in the panel-focus pass: /storage, /services, /network and
// /settings served data the agent cannot see from inside its own
// container (systemd, host netns) or nothing configurable at all.
//
// Unknown paths fall back to `overview` so the SPA never renders a
// blank screen — important when Authelia redirects back with a stale
// `?return_to=` that the server has already dropped.

export type RouteName =
  | "overview"
  | "host"
  | "containers"
  | "container-detail"
  | "audit"
  | "login";

export interface RouteMatch {
  name: RouteName;
  path: string;
  params: Readonly<Record<string, string>>;
  query: URLSearchParams;
}

interface RouteEntry {
  name: RouteName;
  // `:foo` segments denote named parameters. Order matters — the first
  // entry whose pattern matches wins.
  pattern: string;
}

export const ROUTES: readonly RouteEntry[] = [
  { name: "overview", pattern: "/" },
  { name: "host", pattern: "/host" },
  { name: "containers", pattern: "/containers" },
  { name: "container-detail", pattern: "/containers/:id" },
  { name: "audit", pattern: "/audit" },
  // /login is the public auth route. Treated specially by
  // App.svelte — when the user is unauthenticated and the active
  // route is anything else, App redirects to `/login?next=<path>`.
  { name: "login", pattern: "/login" },
] as const;

/**
 * Pure resolver — converts a pathname (e.g. `/containers/sola-app`) into
 * a typed `RouteMatch`. Unknown paths resolve to `overview` so the SPA
 * always has something to render.
 */
export function resolveRoute(pathname: string): RouteMatch {
  // Strip the query string and split into segments.
  const [pathOnly, search = ""] = pathname.split("?", 2);
  const query = new URLSearchParams(search);
  const segments = pathOnly.split("/").filter(Boolean);

  for (const entry of ROUTES) {
    const params = matchSegments(entry.pattern, segments);
    if (params) {
      return {
        name: entry.name,
        path: pathOnly,
        params,
        query,
      };
    }
  }

  return {
    name: "overview",
    path: "/",
    params: {},
    query,
  };
}

/**
 * Look up the registry entry whose pattern matches the literal path
 * (no `:id` resolution — this is the building block the tests use to
 * confirm the registry covers every page).
 */
export function matchPath(literal: string): { name: RouteName } {
  const segments = literal.split("/").filter(Boolean);
  for (const entry of ROUTES) {
    const params = matchSegments(entry.pattern, segments);
    if (params) return { name: entry.name };
  }
  return { name: "overview" };
}

function matchSegments(
  pattern: string,
  segments: readonly string[],
): Record<string, string> | null {
  const patternSegs = pattern.split("/").filter(Boolean);
  if (patternSegs.length !== segments.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternSegs.length; i += 1) {
    const p = patternSegs[i]!;
    const s = segments[i]!;
    if (p.startsWith(":")) {
      // Parameter segment — must be a non-empty single segment (no slashes).
      if (s.length === 0) return null;
      params[p.slice(1)] = decodeURIComponent(s);
    } else if (p !== s) {
      return null;
    }
  }
  return params;
}

// --- imperative bridge ---------------------------------------------------
// Components subscribe to URL changes via `subscribeToRoute()`. The
// router listens for `popstate` (browser back/forward) and exposes
// `push()` for in-app navigation. We keep this separate from
// `resolveRoute()` so the pure function stays trivially testable.

export type RouteListener = (match: RouteMatch) => void;

export function currentRoute(): RouteMatch {
  return resolveRoute(window.location.pathname + window.location.search);
}

export function push(path: string): void {
  if (window.location.pathname + window.location.search === path) return;
  window.history.pushState(null, "", path);
  emit(resolveRoute(path));
}

export function replace(path: string): void {
  window.history.replaceState(null, "", path);
  emit(resolveRoute(path));
}

export function subscribeToRoute(listener: RouteListener): () => void {
  listeners.add(listener);
  // Prime the listener with the current route so consumers can render
  // synchronously on mount.
  listener(currentRoute());
  return () => {
    listeners.delete(listener);
  };
}

const listeners: Set<RouteListener> = new Set();

function emit(match: RouteMatch): void {
  for (const l of listeners) l(match);
}

// Wire `popstate` once at module load. Idempotent across HMR reloads.
if (typeof window !== "undefined" && !(window as unknown as { __routerWired?: boolean }).__routerWired) {
  (window as unknown as { __routerWired?: boolean }).__routerWired = true;
  window.addEventListener("popstate", () => emit(currentRoute()));
}
